package floris.graphics.backoffice.pages.admin.projects

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.core.constants.Res
import floris.graphics.core.navigation.Navigation
import com.materialdesignsystem.components.widgets.DsCard
import com.materialdesignsystem.components.widgets.DsEmptyScreen
import com.materialdesignsystem.components.widgets.DsSpinner
import com.materialdesignsystem.components.widgets.SpinnerSize
import com.materialdesignsystem.extensions.maxLines
import com.materialdesignsystem.toColorScheme
import floris.graphics.models.domain.models.project.Project
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A

@Page("/admin/projects")
@Composable
fun ProjectsPage() {
    val context = rememberPageContext()

    val viewModel = remember { ProjectsViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collectLatest {
            when (it) {
                is ProjectsUiEvent.NavigateToManageProject -> {
                    val route = Navigation.Screen.Admin.Projects.Manage(it.projectId).route
                    context.router.navigateTo(route)
                }
            }
        }
    }

    ProjectsContent(
        state = state,
        onEvent = viewModel::onEvent
    )
}

@Composable
private fun ProjectsContent(
    state: ProjectsState,
    onEvent: (ProjectsEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()


    PageLayout(
        title = "Projects",
        fabOnClick = { onEvent(ProjectsEvent.NavigateToManageProject()) }
    ) {
        if (state.isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                DsSpinner(size = SpinnerSize.Large)
            }

            return@PageLayout
        }

        if (!state.isLoading && state.projects.isEmpty()) {
            DsEmptyScreen(
                title = "Geen projecten gevonden",
                description = "Maak jou eerste project aan om deze daarna hier terug te vinden!",
                actionButtonText = "Voeg een nieuwe project toe",
                onActionButtonClick = { onEvent(ProjectsEvent.NavigateToManageProject()) }
            )

            return@PageLayout
        }

        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px)
                .gap(
                    rowGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px,
                    columnGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px
                ),
            numColumns = numColumns(1, xl = 2)
        ) {
            state.projects.forEach { project ->
                ProjectCard(project = project)
            }
        }
    }
}

@Composable
fun ProjectCard(
    project: Project
) {
    A(
        href = Navigation.Screen.Admin.Projects.Manage(project.id).route,
        attrs = Modifier
            .textDecorationLine(TextDecorationLine.None)
            .toAttrs()
    ) {
        DsCard(
            contentPadding = 0.px,
            content = {
                ProjectCardContent(project)
            }
        )
    }
}

@Suppress("MagicNumber")
@Composable
private fun ProjectCardContent(project: Project) {
    val colorScheme = ColorMode.current.toColorScheme

    Image(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(topLeft = 8.px, topRight = 8.px)
            .aspectRatio(16, 9)
            .objectFit(ObjectFit.Cover)
            .attrsModifier {
                attr("onerror", "this.onerror=null; this.src='${Res.Image.logo}'")
            },
        src = project.thumbnailMediaFile?.url.toString(),
        description = "Post Thumbnail Image"
    )

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 38.px, bottom = 30.px),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        SpanText(
            modifier = Modifier
                .fillMaxWidth()
                .color(colorScheme.onBackground)
                .fontSize(20.px)
                .fontWeight(FontWeight.Bold)
                .textOverflow(TextOverflow.Ellipsis)
                .overflow(Overflow.Hidden),
            text = project.title
        )

        SpanText(
            modifier = Modifier
                .fillMaxWidth()
                .color(colorScheme.onBackground)
                .fontSize(16.px)
                .textOverflow(TextOverflow.Ellipsis)
                .overflow(Overflow.Hidden)
                .maxLines(3),
            text = project.subtitle
        )
    }
}
